import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { eventsService, photosService } from '../../services/firestoreService.js';
import { useAuth } from '../../hooks/useAuth';
import { collection, query, where, orderBy, onSnapshot, limit, getDocs, startAfter } from 'firebase/firestore';
import { db } from '../../firebase/config.js';
import { Calendar, Users, Camera, ChevronLeft, ChevronRight, Check, X, Trash2, Link as LinkIcon, Download } from 'lucide-react';
import toast from 'react-hot-toast';
import ConfirmDialog from '../Common/ConfirmDialog';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';

export const EventPhotos = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const [event, setEvent] = useState(null);
  const [approved, setApproved] = useState([]);
  const [pending, setPending] = useState([]);
  const [rejected, setRejected] = useState([]);
  const [tab, setTab] = useState('approved'); // approved | pending | rejected
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [lightIdx, setLightIdx] = useState(-1);
  const [approvedCursor, setApprovedCursor] = useState(null);
  const [pendingCursor, setPendingCursor] = useState(null);
  const [rejectedCursor, setRejectedCursor] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [deleteState, setDeleteState] = useState({ open: false, loading: false, items: [] });
  const [downloadState, setDownloadState] = useState({ open: false, loading: false, total: 0, done: 0, fail: 0 });

  useEffect(() => {
    if (!id) return;
    let unsubApproved = null, unsubPending = null, unsubRejected = null;
    setLoading(true);
    setError('');
    (async () => {
      try {
        const ev = await eventsService.getEventById(id);
        setEvent(ev);
      } catch (e) {
        console.error(e);
      }
      try {
        const col = collection(db, 'photos');
        unsubApproved = onSnapshot(
          query(col, where('eventId','==', id), where('status','==','approved'), orderBy('uploadedAt','desc'), limit(60)),
          snap => { setApproved(snap.docs.map(d => ({ photoId: d.id, ...d.data() }))); setApprovedCursor(snap.docs.length ? snap.docs[snap.docs.length-1] : null); }
        );
        unsubPending = onSnapshot(
          query(col, where('eventId','==', id), where('status','==','pending'), orderBy('uploadedAt','desc'), limit(60)),
          snap => { setPending(snap.docs.map(d => ({ photoId: d.id, ...d.data() }))); setPendingCursor(snap.docs.length ? snap.docs[snap.docs.length-1] : null); }
        );
        unsubRejected = onSnapshot(
          query(col, where('eventId','==', id), where('status','==','rejected'), orderBy('uploadedAt','desc'), limit(60)),
          snap => { setRejected(snap.docs.map(d => ({ photoId: d.id, ...d.data() }))); setRejectedCursor(snap.docs.length ? snap.docs[snap.docs.length-1] : null); }
        );
      } catch (e) {
        console.error(e);
        setError('No se pudieron cargar las fotos del evento');
      } finally {
        setLoading(false);
      }
    })();
    return () => { try { unsubApproved && unsubApproved(); unsubPending && unsubPending(); unsubRejected && unsubRejected(); } catch (_) {} };
  }, [id]);

  const list = useMemo(() => {
    const base = tab === 'approved' ? approved : tab === 'pending' ? pending : rejected;
    if (!search) return base;
    const q = search.toLowerCase();
    return base.filter(p => (p.userName || '').toLowerCase().includes(q));
  }, [approved, pending, rejected, tab, search]);

  const getStatusPill = (status) => ({
    active: 'bg-green-100 text-green-800',
    upcoming: 'bg-blue-100 text-blue-800',
    completed: 'bg-gray-100 text-gray-800',
    draft: 'bg-yellow-100 text-yellow-800',
    paused: 'bg-orange-100 text-orange-800'
  }[status] || 'bg-gray-100 text-gray-800');

  const copyInviteLink = () => {
    if (!event?.joinCode) return;
    const url = `${window.location.origin}/shot?code=${event.joinCode}`;
    try { navigator.clipboard.writeText(url); toast.success('Enlace copiado'); } catch (_) { toast.success('Enlace listo'); }
  };

  const moderate = useCallback(async (photoId, next) => {
    if (!user) return;
    try {
      await photosService.moderatePhoto(photoId, next, user.uid);
      if (next === 'approved') toast.success('Foto aprobada');
      else if (next === 'rejected') toast.success('Foto rechazada');
    } catch (e) {
      console.error(e);
      toast.error('No se pudo actualizar la foto');
    }
  }, [user]);

  const toggleSelected = (pid) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(pid)) next.delete(pid); else next.add(pid);
      return next;
    });
  };

  const clearSelection = () => setSelected(new Set());

  const moderateSelected = async (next) => {
    if (selected.size === 0) return;
    try {
      const ids = Array.from(selected);
      await Promise.all(ids.map(id => photosService.moderatePhoto(id, next, user?.uid)));
      clearSelection();
      toast.success(next === 'approved' ? 'Fotos aprobadas' : 'Fotos actualizadas');
    } catch (e) {
      console.error(e);
      toast.error('Error en acción masiva');
    }
  };

  const loadMore = async () => {
    if (!id) return;
    const col = collection(db, 'photos');
    let cursor = tab === 'approved' ? approvedCursor : tab === 'pending' ? pendingCursor : rejectedCursor;
    if (!cursor) return;
    setLoadingMore(true);
    try {
      const q = query(col, where('eventId','==', id), where('status','==', tab), orderBy('uploadedAt','desc'), startAfter(cursor), limit(60));
      const snap = await getDocs(q);
      const more = snap.docs.map(d => ({ photoId: d.id, ...d.data() }));
      const mergeUnique = (arr, extra) => {
        const seen = new Set(arr.map(p => p.photoId));
        const newOnes = extra.filter(p => !seen.has(p.photoId));
        return arr.concat(newOnes);
      };
      if (tab === 'approved') {
        setApproved(a => mergeUnique(a, more));
        setApprovedCursor(snap.docs.length ? snap.docs[snap.docs.length-1] : null);
      } else if (tab === 'pending') {
        setPending(a => mergeUnique(a, more));
        setPendingCursor(snap.docs.length ? snap.docs[snap.docs.length-1] : null);
      } else {
        setRejected(a => mergeUnique(a, more));
        setRejectedCursor(snap.docs.length ? snap.docs[snap.docs.length-1] : null);
      }
    } catch (e) {
      console.error(e);
      toast.error('No se pudieron cargar más fotos');
    } finally {
      setLoadingMore(false);
    }
  };

  const askDeleteOne = (photo) => {
    setDeleteState({ open: true, loading: false, items: [{ id: photo.photoId, url: photo.imageURL || photo.thumbnailURL }] });
  };

  const askDeleteBulk = () => {
    const base = tab === 'approved' ? approved : tab === 'pending' ? pending : rejected;
    const items = base.filter(p => selected.has(p.photoId)).map(p => ({ id: p.photoId, url: p.imageURL || p.thumbnailURL }));
    if (items.length === 0) return;
    setDeleteState({ open: true, loading: false, items });
  };

  const confirmDelete = async () => {
    try {
      setDeleteState(s => ({ ...s, loading: true }));
      const items = deleteState.items;
      await Promise.all(items.map(it => photosService.deletePhoto(it.id, it.url)));
      // Optimista: remover local si snapshot aún no llegó
      const ids = new Set(items.map(i => i.id));
      setApproved(a => a.filter(p => !ids.has(p.photoId)));
      setPending(a => a.filter(p => !ids.has(p.photoId)));
      setRejected(a => a.filter(p => !ids.has(p.photoId)));
      clearSelection();
      toast.success(items.length > 1 ? 'Fotos eliminadas' : 'Foto eliminada');
      setDeleteState({ open: false, loading: false, items: [] });
    } catch (e) {
      console.error(e);
      toast.error('No se pudieron eliminar');
      setDeleteState(s => ({ ...s, loading: false }));
    }
  };

  const cancelDelete = () => setDeleteState({ open: false, loading: false, items: [] });

  const askDownloadAll = () => setDownloadState({ open: true, loading: false, total: 0, done: 0, fail: 0 });

  const downloadAll = async () => {
    if (!id) return;
    setDownloadState(s => ({ ...s, loading: true, done: 0, fail: 0, total: 0 }));
    try {
      // Recoger todas las fotos del evento (todas las pestañas)
      const col = collection(db, 'photos');
      let items = [];
      let last = null;
      // Paginamos para evitar timeouts en eventos grandes
      while (true) {
        const q = last
          ? query(col, where('eventId','==', id), orderBy('uploadedAt','desc'), startAfter(last), limit(200))
          : query(col, where('eventId','==', id), orderBy('uploadedAt','desc'), limit(200));
        const snap = await getDocs(q);
        if (snap.empty) break;
        items = items.concat(snap.docs.map(d => ({ id: d.id, ...(d.data()) })));
        last = snap.docs[snap.docs.length-1];
        if (snap.size < 200) break;
      }
      if (!items.length) { toast('No hay fotos para descargar'); setDownloadState({ open:false, loading:false, done:0, fail:0, total:0 }); return; }
      setDownloadState(s => ({ ...s, total: items.length }));
      const zip = new JSZip();
      const folder = zip.folder(event?.name || `evento_${id}`) || zip;
      let ok = 0, fail = 0;
      // Descargar en lotes para no saturar
      const batchSize = 6;
      for (let i=0; i<items.length; i+=batchSize) {
        const batch = items.slice(i, i+batchSize);
        await Promise.all(batch.map(async (p, idx) => {
          const url = p.imageURL || p.thumbnailURL;
          if (!url) { fail++; return; }
          try {
            const resp = await fetch(url, { mode: 'cors' });
            if (!resp.ok) throw new Error('fetch-failed');
            const blob = await resp.blob();
            const ext = (blob.type && blob.type.split('/')[1]) || 'jpg';
            const base = (p.userName ? p.userName.replace(/[^a-z0-9_\-]+/gi,'_') + '_' : '') + (p.id || (i+idx));
            const name = `${base}.${ext}`;
            folder.file(name, blob);
            ok++;
          } catch (e) { fail++; }
          finally {
            setDownloadState(s => ({ ...s, done: ok + fail, fail }));
          }
        }));
      }
      const out = await zip.generateAsync({ type: 'blob' });
      const fileName = `${(event?.name || 'evento')}_fotos_${new Date().toISOString().slice(0,10)}.zip`;
      saveAs(out, fileName);
      toast.success(`Descarga lista (${ok} fotos${fail?`, ${fail} con error`:''})`);
      setDownloadState({ open:false, loading:false, total: items.length, done: items.length, fail });
    } catch (e) {
      console.error(e);
      toast.error('No se pudo generar el ZIP');
      setDownloadState({ open:false, loading:false, total:0, done:0, fail:0 });
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center min-h-96">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-indigo-600"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded">{error}</div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      

      {/* Acciones */}
      <div className="flex items-center justify-between mb-3">
        <div className="font-semibold text-gray-900">Fotos del evento</div>
        <button onClick={askDownloadAll} className="px-3 py-1.5 rounded-md bg-gray-900 text-white text-sm flex items-center gap-1">
          <Download className="w-4 h-4"/> Descargar todas
        </button>
      </div>

      {/* Tabs + Search */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <button className={`px-3 py-1.5 rounded-md text-sm ${tab==='approved'?'bg-gray-900 text-white':'bg-gray-100 text-gray-800'}`} onClick={()=>setTab('approved')}>Aprobadas ({approved.length})</button>
          <button className={`px-3 py-1.5 rounded-md text-sm ${tab==='pending'?'bg-amber-600 text-white':'bg-amber-100 text-amber-900'}`} onClick={()=>setTab('pending')}>Pendientes ({pending.length})</button>
          <button className={`px-3 py-1.5 rounded-md text-sm ${tab==='rejected'?'bg-red-600 text-white':'bg-red-100 text-red-900'}`} onClick={()=>setTab('rejected')}>Rechazadas ({rejected.length})</button>
        </div>
        <input value={search} onChange={(e)=>setSearch(e.target.value)} placeholder="Buscar por nombre de usuario" className="px-3 py-2 border border-gray-300 rounded-md w-full md:w-72" />
      </div>

      {/* Bulk actions bar */}
      {selected.size > 0 && (
        <div className="mb-3 p-3 bg-indigo-50 border border-indigo-200 rounded flex items-center gap-2 text-sm">
          <span className="font-medium text-indigo-900">{selected.size} seleccionada(s)</span>
          {tab !== 'approved' && (
            <button onClick={() => moderateSelected('approved')} className="px-3 py-1 rounded bg-emerald-600 text-white text-xs">Aprobar</button>
          )}
          {tab !== 'rejected' && (
            <button onClick={() => moderateSelected('rejected')} className="px-3 py-1 rounded bg-red-600 text-white text-xs">Rechazar</button>
          )}
          {tab === 'rejected' && (
            <button onClick={askDeleteBulk} className="px-3 py-1 rounded bg-white text-red-700 border border-red-300 text-xs flex items-center gap-1"><Trash2 className="w-3.5 h-3.5"/>Eliminar definitivamente</button>
          )}
          <button onClick={clearSelection} className="px-3 py-1 rounded bg-white text-gray-700 border text-xs">Limpiar</button>
        </div>
      )}

      {/* Grid */}
      {list.length === 0 ? (
        <div className="text-gray-600">No hay fotos para mostrar en esta pestaña.</div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
          {list.map((p, idx) => (
            <div key={p.photoId} className="border rounded overflow-hidden bg-white group relative">
              {/* checkbox selección */}
              <label className="absolute top-2 left-2 z-10 flex items-center gap-1 bg-white/80 backdrop-blur px-1.5 py-0.5 rounded">
                <input type="checkbox" checked={selected.has(p.photoId)} onChange={() => toggleSelected(p.photoId)} />
              </label>
              <img onClick={()=>setLightIdx(idx)} src={p.thumbnailURL || p.imageURL} alt={p.userName || 'Foto'} className="w-full h-40 object-cover cursor-zoom-in" />
              <div className="p-2 text-xs text-gray-600 flex items-center justify-between">
                <span className="truncate" title={p.userName || 'Invitado'}>{p.userName || 'Invitado'}</span>
                <span>{p.uploadedAt?.toDate ? new Date(p.uploadedAt.toDate()).toLocaleString() : ''}</span>
              </div>
              {tab === 'pending' && (
                <div className="p-2 border-t flex items-center gap-2">
                  <button onClick={()=>moderate(p.photoId,'approved')} className="flex-1 px-2 py-1 text-xs rounded-md bg-emerald-600 text-white hover:bg-emerald-700 flex items-center justify-center"><Check className="w-3.5 h-3.5 mr-1"/>Aprobar</button>
                  <button onClick={()=>moderate(p.photoId,'rejected')} className="flex-1 px-2 py-1 text-xs rounded-md bg-red-600 text-white hover:bg-red-700 flex items-center justify-center"><X className="w-3.5 h-3.5 mr-1"/>Rechazar</button>
                </div>
              )}
              {tab === 'approved' && (
                <div className="p-2 border-t flex items-center gap-2">
                  <button onClick={()=>moderate(p.photoId,'rejected')} className="flex-1 px-2 py-1 text-xs rounded-md bg-red-50 text-red-700 hover:bg-red-100">Quitar</button>
                </div>
              )}
              {tab === 'rejected' && (
                <div className="p-2 border-t flex items-center gap-2">
                  <button onClick={()=>moderate(p.photoId,'approved')} className="flex-1 px-2 py-1 text-xs rounded-md bg-emerald-600 text-white hover:bg-emerald-700 flex items-center justify-center"><Check className="w-3.5 h-3.5 mr-1"/>Aprobar</button>
                  <button onClick={()=>askDeleteOne(p)} className="flex-1 px-2 py-1 text-xs rounded-md bg-white text-red-700 border border-red-300 hover:bg-red-50 flex items-center justify-center"><Trash2 className="w-3.5 h-3.5 mr-1"/>Eliminar</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Load more */}
      <div className="mt-4 flex items-center justify-center">
        {((tab === 'approved' && approvedCursor) || (tab === 'pending' && pendingCursor) || (tab === 'rejected' && rejectedCursor)) && (
          <button disabled={loadingMore} onClick={loadMore} className="px-4 py-2 rounded bg-gray-100 hover:bg-gray-200 text-gray-800 disabled:opacity-50">
            {loadingMore ? 'Cargando…' : 'Cargar más'}
          </button>
        )}
      </div>

      {/* Lightbox */}
      {lightIdx >= 0 && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center" onClick={()=>setLightIdx(-1)}>
          <button className="absolute left-4 top-1/2 -translate-y-1/2 p-2 rounded bg-white/10 text-white" onClick={(e)=>{ e.stopPropagation(); setLightIdx(i=> Math.max(0, i-1)); }}>
            <ChevronLeft className="w-6 h-6" />
          </button>
          <img src={(list[lightIdx]?.imageURL) || (list[lightIdx]?.thumbnailURL)} alt="" className="max-h-[90vh] max-w-[90vw] object-contain" />
          <button className="absolute right-4 top-1/2 -translate-y-1/2 p-2 rounded bg-white/10 text-white" onClick={(e)=>{ e.stopPropagation(); setLightIdx(i=> Math.min(list.length-1, i+1)); }}>
            <ChevronRight className="w-6 h-6" />
          </button>
        </div>
      )}

      {/* Confirm delete */}
      <ConfirmDialog
        isOpen={deleteState.open}
        title={deleteState.items.length > 1 ? 'Eliminar fotos definitivamente' : 'Eliminar foto definitivamente'}
        message={'Esta acción no se puede deshacer. Se eliminará de la galería y del almacenamiento.'}
        confirmText={deleteState.items.length > 1 ? 'Eliminar fotos' : 'Eliminar'}
        variant="danger"
        loading={deleteState.loading}
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
      />

      {/* Confirm download all */}
      <ConfirmDialog
        isOpen={downloadState.open}
        title={'Descargar todas las fotos'}
        message={'Se generará un archivo ZIP con todas las fotos del evento. Esto puede tardar varios minutos según la cantidad de imágenes.'}
        confirmText={'Generar ZIP'}
        variant="default"
        loading={downloadState.loading}
        onConfirm={downloadAll}
        onCancel={()=> setDownloadState({ open:false, loading:false })}
      />

      {/* Progress overlay */}
      {downloadState.loading && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-6">
          <div className="bg-white rounded-lg shadow-xl w-[min(92vw,420px)] p-5">
            <div className="text-lg font-semibold mb-2">Generando ZIP</div>
            <div className="text-sm text-gray-600 mb-3">
              {downloadState.done}/{downloadState.total} archivos descargados{downloadState.fail?`, ${downloadState.fail} errores`:''}
            </div>
            <div className="w-full h-2 bg-gray-200 rounded overflow-hidden">
              <div className="h-2 bg-indigo-600 transition-[width] duration-200" style={{ width: `${downloadState.total ? Math.min(100, Math.round((downloadState.done / downloadState.total)*100)) : 0}%` }} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EventPhotos;
